import { Component, OnDestroy, OnInit, AfterViewChecked } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subject, switchMap } from 'rxjs';
import { BenefitsService } from '../../../core/service/benefits.service';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../../../core/service';
import { ActivatedRoute, Router } from '@angular/router';
import { BenefitHelperService } from 'app/core/service/benefit-helper.service';
import { SubdomainInfo } from 'app/core/models/subdomain-info';
import { User } from "../../../core/models";
import { GetProviderName, INITIAL_LOAD_CNT, LOAD_MORE_CNT, PRICING_PLAN_LABEL } from "../../../core/helpers/constants";
import { GetProvidersCardInfoRequest } from "../../../core/requests/GetProvidersCardInfoRequest";
import { ProviderCard } from "../../../core/models/provider-card";
import { OfferBenefits } from "../../../core/models/offer-benefits";
import { DashboardService } from '../../../core/service/dashboard.service';
import { EventModel } from '../../../core/models/EventModel';
import {GetCompanyEventsResponse} from "../../../core/responses/GetCompanyEventsResponse";

@Component({
  selector: 'app-flexible-benefits',
  templateUrl: './flexible-benefits.component.html',
  styleUrls: ['./flexible-benefits.component.scss'],
  providers: [TranslatePipe],
})
export class FlexibleBenefitsComponent implements OnInit, OnDestroy, AfterViewChecked {
  // Scroll-management variables
  private static savedScrollPosition = 0;
  private savedFilterScrollPosition = 0;
  private shouldRestoreScroll = false;

  private ngUnsubscribe = new Subject<void>();
  public subdomain: string;
  public userCompanyInfo: SubdomainInfo;
  private initialProvidersNotFiltered: boolean = false;
  providersCardInfo: ProviderCard[] = [];
  totalProviders: number;
  providersOutdated: boolean = false;
  offerBenefits: OfferBenefits[];
  // Each selected filter before pressing the 'Apply filter' button
  // is stored in this variable
  unconfirmedFilters: any = {};
  appliedFilters: any = {};
  showBudgets: boolean;
  dataLoadingDone = false;
  public activeEvents: EventModel[] = [];

  constructor(
    private benefitHelper: BenefitHelperService,
    private benefitsService: BenefitsService,
    private authenticationService: AuthenticationService,
    private _route: ActivatedRoute,
    private router: Router,
    private dashboardService: DashboardService
  ) {
    this.subdomain = this._route.snapshot.params['subdomain'];
  }

  ngOnInit(): void {
    this.initialProvidersNotFiltered = this.initAppliedFilters();

    // This is needed to make sure the scroll goes at top when filters change. Required to be placed here because getProvidersCardInfo
    // overwrites the cached filter
    if (this.benefitsService.checkIfSameAppliedFilters(this.appliedFilters, this.benefitsService.getProvidersCachedFilterKey()) === false) {
      FlexibleBenefitsComponent.savedScrollPosition = 0;
    }

    this.dashboardService.getCompanyEvents(this.subdomain)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((companyEventsResponse) => {
          this.onGetCompanyEventsSuccess(companyEventsResponse);
          return forkJoin({
            // For now, it is safe to assume the maximum number of events is less than INITIAL_LOAD_CNT
            // TODO: Firstly load the events until all of them are received using the 'Load more' button, then load the providers
            //  getCompanyEvents() should be refactored to return paginated results with start and limit parameters,
            //  similar to getProvidersCardInfo().
            providersResponse: this.getProvidersCardInfo(0, INITIAL_LOAD_CNT - this.activeEvents.length),
            benefitOffersResponse: this.getBenefitOffers()
          });
        })
      )
      .subscribe({
        next: ({providersResponse, benefitOffersResponse}) => {
          this.onGetProvidersCardInfoSuccess(providersResponse);
          this.onGetBenefitOffersSuccess(benefitOffersResponse.responseObject);
          this.restoreScroll();
        },
        error: error => {
          this.onGetProvidersCardInfoFail(error);
          this.onGetBenefitOffersFail(error);
          this.onGetCompanyEventsFail(error);
        }
      });

    this.authenticationService.currentUserCompanyInfo.subscribe((companyInfo) => {
      this.userCompanyInfo = companyInfo;
    });
    this.showBudgets = this.userCompanyInfo.hasTransportBudget || this.userCompanyInfo.hasProfessionalDevelopmentBudget;
  }

  ngAfterViewChecked(): void {
    // Required to prevent scroll jumping to top on filter change
    // The scroll will be restored in the next tick after the view is updated with the new benefits.
    // TODO: Maybe find a better solution than this
    if (this.shouldRestoreScroll && this.savedFilterScrollPosition) {
      window.scrollTo(0, this.savedFilterScrollPosition);
      this.shouldRestoreScroll = false;
    }
  }

  public ngOnDestroy() {
    FlexibleBenefitsComponent.savedScrollPosition = window.scrollY;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Get benefit offers and provider card info - main data for the page
   */

  public getBenefitOffers() {
    return this.benefitsService
      .getOfferBenefits(this.authenticationService.currentUserValue.companyId)
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  public onGetBenefitOffersSuccess(offerBenefits: OfferBenefits[]) {
    // Sort the offers by the discountPercentage from highest to the lowest - maybe this will be changed in the future
    this.offerBenefits = offerBenefits
      .filter((b) => !b.isBusinessProduct)
      .map(b => {
        const realOfferBenefit: OfferBenefits = Object.assign(new OfferBenefits(), b);

        return this.benefitHelper.mapBenefitToDisplayPrice(realOfferBenefit) as OfferBenefits;
      })
      .sort((a, b) => b.discountPercentage - a.discountPercentage);
  }

  public onGetBenefitOffersFail(error: Error) {}

  public getProvidersCardInfo(start: number = 0, limit: number = INITIAL_LOAD_CNT) {
    let params: GetProvidersCardInfoRequest = {
      paginationCriteria: {
        start: start,
        limit: limit
      },
      companyId: this.authenticationService.currentUserValue.companyId,
    };
    params = this.populateParamsWithFilters(params);

    return this.benefitsService
      .getUserProvidersCardInfo(params)
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  public getProvidersCardInfoWithSubscription(start: number = 0, limit: number = INITIAL_LOAD_CNT) {
    const req = this.getProvidersCardInfo(start, limit);
    req.subscribe(
      (data) => {
        this.onGetProvidersCardInfoSuccess(data);
      },
      (error) => {
        this.onGetProvidersCardInfoFail(error);
      }
    );
  }

  public onGetProvidersCardInfoSuccess(response: {totalResults: number, data: ProviderCard[]}) {
    const receivedProviders = response.data.map((providerCard) => {
      const card = Object.assign(new ProviderCard(), providerCard);
      card.name = GetProviderName(card.name, this.subdomain);
      return card;
    });
    this.providersCardInfo = [...this.providersCardInfo, ...receivedProviders];
    this.totalProviders = response.totalResults;
    this.dataLoadingDone = true;
  }

  public onGetProvidersCardInfoFail(error: HttpErrorResponse) {
    this.dataLoadingDone = true;
  }

  public onGetCompanyEventsSuccess(response: GetCompanyEventsResponse) {
    const hasGiftQuota = (this.authenticationService.currentUserInfoValue?.numberOfGiftTickets ?? 0) > 0;
    if (hasGiftQuota && response.responseObject?.length) {
      const today = new Date();
      this.activeEvents = response.responseObject.filter(e => {
        const start = new Date(e.startDate);
        const end = new Date(e.endDate);
        end.setHours(23, 59, 59, 999);
        return start <= today && today <= end;
      });
    }
  }

  public onGetCompanyEventsFail(error: HttpErrorResponse) {}

  /**
   * Helpers
   */

  private initAppliedFilters() {
    let hasInitAppliedFilters = false;
    const queryParams = this._route.snapshot.queryParams;

    const parseParam = (param: any): number[] => {
      if (Array.isArray(param)) {
        return param.map(Number);
      } else if (typeof param === 'string') {
        return param.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
      } else if (typeof param === 'number') {
        return [param];
      }
      return [];
    };

    ['benefit_category', 'tax_category', 'budgets', 'locations'].forEach(key => {
      if (queryParams[key]) {
        const arr = parseParam(queryParams[key]);
        if (arr.length > 0) {
          this.unconfirmedFilters[key] = arr;
          this.appliedFilters[key] = arr;
          hasInitAppliedFilters = true;
        }
      }
    });

    return hasInitAppliedFilters;
  }

  public loadMoreProviders() {
    if (this.providersCardInfo && this.providersCardInfo.length === this.totalProviders)
      return;

    const lim = Math.min(LOAD_MORE_CNT, this.totalProviders - this.providersCardInfo.length);

    this.getProvidersCardInfoWithSubscription(this.providersCardInfo.length, lim);
  }

  public openFlexibleSupplierInfoPage(providerId: number) {
    const queryParams: any = this._route.snapshot.queryParams;
    this.router.navigate([
      '/' + this.subdomain + '/flexible-supplier-info/' + providerId,
    ], { queryParams });
  }

  public changeFilters(filters){
    this.unconfirmedFilters = {...this.unconfirmedFilters, ...filters};
    this.savedFilterScrollPosition = window.scrollY;
    this.shouldRestoreScroll = true;
    if (!this.initialProvidersNotFiltered)
      this.providersOutdated = true;
    else {
      this.initialProvidersNotFiltered = false;
    }
  }

  public applyFilters() {
    if (this.providersOutdated) {
      this.appliedFilters = {...this.unconfirmedFilters};
      this.providersOutdated = false;
      this.resetScrollAndProviders();
    }
  }

  public openEventPage(eventId: number) {
    this.router.navigate(['/' + this.subdomain + '/event-supplier-info/' + eventId]);
  }

  public resetAllFilters() {
    this.appliedFilters={};
    this.unconfirmedFilters={};
    this.providersOutdated = false;
    this.resetScrollAndProviders();
  }

  private populateParamsWithFilters(params: GetProvidersCardInfoRequest): GetProvidersCardInfoRequest {
    if (!params.appliedFilters) {
      params.appliedFilters = {}
    }

    if(this.appliedFilters.benefit_category?.length > 0) {
      params.appliedFilters.categoryIds = this.appliedFilters.benefit_category;
    }
    if(this.appliedFilters.tax_category?.length > 0) {
      params.appliedFilters.benefitTaxCategories = this.appliedFilters.tax_category;
    }
    if(this.appliedFilters.locations?.length > 0) {
      params.appliedFilters.locations = this.appliedFilters.locations;
    }
    if(this.appliedFilters.budgets?.length > 0) {
      params.appliedFilters.benefitTypes = this.appliedFilters.budgets;
    }

    return params;
  }

  private restoreScroll() {
    setTimeout(() => {
      window.scrollTo(0, FlexibleBenefitsComponent.savedScrollPosition);
    }, 0);
  }

  private resetScrollAndProviders() {
    FlexibleBenefitsComponent.savedScrollPosition = 0;
    setTimeout(() => window.scrollTo(0, FlexibleBenefitsComponent.savedScrollPosition), 0);

    // Clear & re-fetch providers
    this.providersCardInfo = [];
    this.totalProviders = 0;
    this.benefitsService.clearProvidersCardInfoCache();
    this.getProvidersCardInfoWithSubscription();
  }

  protected readonly User = User;
  protected readonly PRICING_PLAN_LABEL = PRICING_PLAN_LABEL;
}
